import React, { FC, useContext, useEffect, useState } from 'react'

import moment from 'moment'
import { ReceiptText, Trash } from 'lucide-react'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { Link } from 'react-router-dom'

import customImageComponent from '../../../../common/CustomImageComponent'
import { Button } from '../../../../ui/button'
import { Badge } from '../../../../ui/badge'
import { HeaderInfoEmpty } from '../empty/HeaderInfoEmpty/HeaderInfoEmpty'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../ui/tooltip'

import { useChatClient } from '../../../../../pages/messages/components/withStreamClient'

import { HandleModalContext } from '../../../../../layout/mainlayout/MainLayout'
import { getUserFullName } from '../../../../../helpers/helpers'

import { useAppSelector } from '../../../../../store/hooks'
import { selectProfileData } from '../../../../../store/user/userSlice'

import { IUserInterface } from '../../../../../models/users/user.interface'
import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'
import { TEventRegisteredUsers } from '../../../../../models/event-registered-users/event-registered-users.interface'
import { IEventReviewPublish } from '../../../../../models/event-review-publish/event-review-publish.interface'
import { IRecipientInterface } from '../../../../../models/recipients/recipients.interface'

import { CONST } from '../../../../../const/const'
import { MODAL_CONSTS } from '../../../../../const/modal-const'
import { USE_MODE_CONST } from '../../../../../const/use-mode'

interface RegistrarHeaderInfoProps {
  user: IUserInterface | null
  eventId: string
  registrationId: string
  riders?: IRegistrationByDayInterface[]
  eventRegisteredUser: TEventRegisteredUsers | null
  eventReviewPublish: IEventReviewPublish | null
  recipient?: IRecipientInterface
  loading: boolean
  activeTab: string
}

export const RegistrarHeaderInfo: FC<RegistrarHeaderInfoProps> = ({
  user,
  eventId,
  registrationId,
  riders,
  eventRegisteredUser,
  eventReviewPublish,
  recipient,
  loading,
  activeTab,
}) => {
  const handleModalContext = useContext(HandleModalContext)

  const { createChatEvent } = useChatClient()

  const profileData = useAppSelector(selectProfileData)

  const [messageLoading, setMessageLoading] = useState(false)
  const [countRiders, setCountRiders] = useState(0)

  const onMessage = async () => {
    if (eventId && user?.id && getUserFullName(user)) {
      try {
        setMessageLoading(true)
        await createChatEvent(
          [
            {
              ...user,
              name: getUserFullName(user),
              role: 'Registrar',
              vertical: 'exhibitors',
            },
          ],
          eventId,
          { eventId }
        )

        const url = `${CONST.ROUTES.MANAGE.CLINIC_N_OTHER.URL}/${eventId}/messages`
        window.open(url, '_blank')
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setMessageLoading(false)
      }
    }
  }

  const onScratch = () => {
    handleModalContext?.handleModal?.(true, MODAL_CONSTS.SCRATCH_FULL_ENTRY, {
      ...handleModalContext?.dataToPassOn,
      userId: user?.id,
      registrationId: registrationId,
      eventId: eventId,
      isRegistrar: true,
      isChargeSalesTax: eventReviewPublish?.EventPaymentSettings?.chargeSalesTax ?? false,
      onSave: () => {
        handleModalContext?.handleModal?.(false, MODAL_CONSTS.REGISTRAR_INFO)
        setTimeout(() => {
          handleModalContext?.handleModal?.(true, MODAL_CONSTS.REGISTRAR_INFO, {
            ...handleModalContext?.dataToPassOn,
          })
        }, 100)
      },
    })
  }

  useEffect(() => {
    const count = new Set()
    riders?.forEach((rider) => {
      count.add(rider.riderId)
    })
    setCountRiders(count.size || 0)
  }, [riders])

  const allowScratches =
    eventReviewPublish?.EventDetails?.allowScratches || profileData.id === eventReviewPublish?.owner

  if (loading) return <HeaderInfoEmpty />

  return (
    <div className="flex flex-wrap justify-between items-center gap-5">
      <div className="flex gap-5 sm:items-center flex-col sm:flex-row">
        {customImageComponent(
          user?.userProfilePicture,
          user ? getUserFullName(user) : 'N/A',
          'w-16 h-16 min-w-16 border border-black/10 rounded-full'
        )}
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h4 className="font-bold text-xl">{user ? getUserFullName(user) : 'N/A'}</h4>
            <span className="py-1 rounded-full px-4 text-sm bg-PurpleThemeColor/10 text-PurpleThemeColor">
              Registrar
            </span>
            <span className="py-1 rounded-full px-4 text-sm bg-PurpleThemeColor/10 text-PurpleThemeColor">
              {countRiders} riders
            </span>
          </div>
          <div className="flex flex-wrap mt-4 gap-y-3 gap-x-5 flex-col sm:flex-row text-black/50">
            <span className="leading-none">{user?.userEmail}</span>
            {user?.userPhoneNumber && typeof user?.userPhoneNumber !== 'object' && (
              <span className="sm:pl-5 sm:border-l sm:border-l-black/20 leading-none">
                {user.userPhoneNumber}
              </span>
            )}
            <span className="sm:pl-5 sm:border-l sm:border-l-black/20 leading-none">
              Registered {moment(eventRegisteredUser?.created as Date).format('D MMM, YYYY')}
            </span>
          </div>
        </div>
      </div>
      <div className="sm:ml-auto">
        <div className="flex items-center gap-2.5">
          <Tooltip>
            <TooltipTrigger className="w-full">
              <Button
                disabled={!allowScratches}
                variant="outline"
                className="w-10 h-10 rounded-full"
                onClick={onScratch}
              >
                <Trash />
              </Button>
            </TooltipTrigger>
            <TooltipContent hidden={allowScratches}>Disabled by show</TooltipContent>
          </Tooltip>
          <Button
            variant="outline"
            className="w-10 h-10 min-w-10 rounded-full p-1 hover:bg-secondary/80"
            onClick={() => {
              handleModalContext?.handleModal(true, MODAL_CONSTS.PAY_ALL, {
                ...handleModalContext?.dataToPassOn,
                registrationId: registrationId,
                eventId: eventId,
                userId: user?.id,
                registrarId: user?.id,
                defaultOpenedSecondaryAccordion: activeTab,
              })
            }}
          >
            <img src="/assets/og_icons/Banknotes-1.svg" className="w-4" alt="" />
          </Button>
          <Link to={`${CONST.ROUTES.RECEIPT.URL}/${recipient?.registrationDocId}`} target="_blank">
            <Badge variant="outline" className="w-10 h-10 rounded-full">
              <ReceiptText />
            </Badge>
          </Link>
          {profileData.userType === USE_MODE_CONST.ORGANIZER && (
            <Button
              disabled={messageLoading}
              type="button"
              className="rounded-full"
              onClick={onMessage}
            >
              {messageLoading && (
                <AutorenewIcon fontSize="small" className="animate-spin h-4 w-4" />
              )}
              Message
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
